import { TypedNode } from "../../nodes/_Base";
import { ActorType } from "../../poly/registers/nodes/types/Actor";
import { CoreObject } from "../../../core/geometry/Object";
import { MapUtils } from "../../../core/MapUtils";
import { ActorManualTriggersController } from "./actors/ManualTriggersController";
import { NodeContext } from "../../poly/NodeContext";
import { ActorPointerEventsController } from "./actors/ActorsPointerEventsController";
import { ActorHoveredEventsController } from "./actors/ActorsHoveredEventsController";
const ACTOR_BUILDER_NODE_IDS_KEY = "actorBuilderNodeIds";
export class ActorBuilderNode extends TypedNode {
  createNode(node_class, options) {
    return super.createNode(node_class, options);
  }
  children() {
    return super.children();
  }
  nodesByType(type) {
    return super.nodesByType(type);
  }
  childrenAllowed() {
    return true;
  }
}
export class ActorsManager {
  constructor(scene) {
    this.scene = scene;
    this._actorNodes = /* @__PURE__ */ new Set();
    this._onEventTickBound = this._onEventTick.bind(this);
    this._onEventSceneResetBound = this._onEventSceneReset.bind(this);
    this._onEventScenePlayBound = this._onEventScenePlay.bind(this);
    this._onEventScenePauseBound = this._onEventScenePause.bind(this);
  }
  assignActorBuilder(object, node) {
    object.userData[ACTOR_BUILDER_NODE_IDS_KEY] = object.userData[ACTOR_BUILDER_NODE_IDS_KEY] || [];
    object.userData[ACTOR_BUILDER_NODE_IDS_KEY].push(node.graphNodeId());
    this._actorNodes.add(node);
  }
  get manualTriggerController() {
    return this._manualTriggerController = this._manualTriggerController || new ActorManualTriggersController(this);
  }
  get pointerEventsController() {
    return this._pointerEventsController = this._pointerEventsController || new ActorPointerEventsController(this);
  }
  get hoveredEventsController() {
    return this._hoveredEventsController = this._hoveredEventsController || new ActorHoveredEventsController(this);
  }
  tick() {
    var _a, _b;
    (_a = this._manualTriggerController) == null ? void 0 : _a.runTriggers();
    (_b = this._pointerEventsController) == null ? void 0 : _b.runTriggers();
    this.hoveredEventsController.runTriggers();
    this._runOnEventTick();
  }
  runOnEventSceneReset() {
    this.scene.threejsScene().traverse(this._onEventSceneResetBound);
  }
  runOnEventScenePlay() {
    this.scene.threejsScene().traverse(this._onEventScenePlayBound);
    this._makeRequiredObjectAttributesReactive();
  }
  runOnEventScenePause() {
    this.scene.threejsScene().traverse(this._onEventScenePauseBound);
  }
  _runOnEventTick() {
    this.scene.threejsScene().traverse(this._onEventTickBound);
  }
  _onEventTick(object) {
    this._triggerEventNodes(object, ActorType.ON_TICK);
  }
  _onEventSceneReset(object) {
    this._triggerEventNodes(object, ActorType.ON_SCENE_RESET);
  }
  _onEventScenePlay(object) {
    this._triggerEventNodes(object, ActorType.ON_SCENE_PLAY_STATE, 0);
  }
  _onEventScenePause(object) {
    this._triggerEventNodes(object, ActorType.ON_SCENE_PLAY_STATE, 1);
  }
  _triggerEventNodes(object, actorType, outputIndex = 0) {
    const nodeIds = this.objectActorNodeIds(object);
    if (!nodeIds) {
      return;
    }
    for (let nodeId of nodeIds) {
      const node = this.scene.graph.nodeFromId(nodeId);
      if (node) {
        const onEventNodes = node.nodesByType(actorType);
        for (let onEventNode of onEventNodes) {
          onEventNode.runTrigger({ Object3D: object }, outputIndex);
        }
      }
    }
  }
  _makeRequiredObjectAttributesReactive() {
    this.scene.threejsScene().traverse((object) => {
      const getNodesByAttribName = () => {
        const nodeIds = this.objectActorNodeIds(object);
        if (!nodeIds) {
          return;
        }
        const actorBuilderNodes = nodeIds.map((nodeId) => this.scene.graph.nodeFromId(nodeId)).filter((node) => node);
        const onEventObjectAttributeUpdatedNodes = actorBuilderNodes.map((node) => node.nodesByType(ActorType.ON_OBJECT_ATTRIBUTE_UPDATE)).flat();
        const nodesByAttribName2 = MapUtils.groupBy(onEventObjectAttributeUpdatedNodes, (node) => node.attributeName());
        return nodesByAttribName2;
      };
      const getParentNodesByAttribName = () => {
        const parent = object.parent;
        if (!parent) {
          return;
        }
        const nodeIds = parent.userData[ACTOR_BUILDER_NODE_IDS_KEY];
        if (!nodeIds) {
          return;
        }
        const actorBuilderNodes = nodeIds.map((nodeId) => this.scene.graph.nodeFromId(nodeId)).filter((node) => node);
        const onEventChildAttributeUpdatedNodes = actorBuilderNodes.map((node) => node.nodesByType(ActorType.ON_CHILD_ATTRIBUTE_UPDATE)).flat();
        const nodesByAttribName2 = MapUtils.groupBy(onEventChildAttributeUpdatedNodes, (node) => node.attributeName());
        return nodesByAttribName2;
      };
      const nodesByAttribName = getNodesByAttribName();
      const parentNodesByAttribName = getParentNodesByAttribName();
      if (!(nodesByAttribName || parentNodesByAttribName)) {
        return;
      }
      const reactiveAttributeNames = /* @__PURE__ */ new Set();
      nodesByAttribName == null ? void 0 : nodesByAttribName.forEach((nodes, attributeName) => {
        reactiveAttributeNames.add(attributeName);
      });
      parentNodesByAttribName == null ? void 0 : parentNodesByAttribName.forEach((nodes, attributeName) => {
        reactiveAttributeNames.add(attributeName);
      });
      reactiveAttributeNames.forEach((attributeName) => {
        const directActorNodes = nodesByAttribName == null ? void 0 : nodesByAttribName.get(attributeName);
        const parentNodes = parentNodesByAttribName == null ? void 0 : parentNodesByAttribName.get(attributeName);
        CoreObject.makeAttribReactive(object, attributeName, (newVal, oldVal) => {
          const context = { Object3D: object };
          if (directActorNodes) {
            for (let node of directActorNodes) {
              node.runTrigger(context);
            }
          }
          if (parentNodes) {
            const parent = object.parent;
            if (parent) {
              const parentContext = { Object3D: parent };
              for (let parentNode of parentNodes) {
                parentNode.runTrigger(parentContext);
              }
            }
          }
        });
      });
    });
  }
  objectActorNodeIds(object) {
    return object.userData[ACTOR_BUILDER_NODE_IDS_KEY];
  }
  parentActorBuilderNode(node) {
    return node.parentController.findParent((parent) => parent.childrenControllerContext() == NodeContext.ACTOR);
  }
}
